import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui/spinner'

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'hover:bg-primary/90 shadow bg-primary text-primary-foreground',
                blackcrow:
                    'hover:bg-blackcrow/90 shadow bg-blackcrow text-blackcrow-foreground',
                destructive:
                    'hover:bg-destructive/90 shadow-sm bg-destructive text-destructive-foreground',
                outline:
                    'border shadow-sm bg-background border-input text-foreground hover:bg-accent hover:text-accent-foreground dark:border-gray-600 dark:text-gray-200',
                secondary:
                    'hover:bg-secondary/80 shadow-sm bg-secondary text-secondary-foreground',
                success:
                    'hover:bg-success/90 shadow-sm bg-success text-success-foreground',
                warning:
                    'hover:bg-warning/90 shadow-sm bg-warning text-warning-foreground',
                info: 'hover:bg-info/90 shadow-sm bg-info text-info-foreground',
                ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground dark:text-gray-200',
                link: 'underline-offset-4 text-primary hover:underline',
            },
            size: {
                default: 'h-9 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-10 rounded-md px-8',
                icon: 'size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isLoading?: boolean
    loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading = false,
            loadingText,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : 'button'
        // Slot requires a single child, so the loading spinner is only rendered
        // for the plain <button> path; asChild callers manage their own content.
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={asChild ? disabled : disabled || isLoading}
                {...props}
            >
                {!asChild && isLoading ? (
                    <>
                        <LoadingSpinner className="mr-2 size-4" />
                        {loadingText ?? children}
                    </>
                ) : (
                    children
                )}
            </Comp>
        )
    },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
